import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import BaticoEligibilityConfigPanel from '../BaticoEligibilityConfigPanel';
import { Settings, Save, RefreshCw, Euro, Calendar, Percent, Plus, Check, X, Loader2, Archive, Users, Building, Briefcase, Shield, Tag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const _RCDS_TYPES = [
  { id: 'annuel', label: 'RCDS Annuel', icon: Calendar, color: 'emerald' },
  { id: 'nominatif', label: 'RCDS Nominatif', icon: Briefcase, color: 'blue' }
];

const _ESPACES = [
  { id: 'super_admin', label: 'Super Admin', icon: Shield },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'cooper', label: "Cooper's", icon: Building },
  { id: 'member', label: 'Membre', icon: Users },
  { id: 'expert', label: 'Expert', icon: Briefcase }
];

export default function BaticoTabsA({ ctx }) {
  const { activeRcds, config: config, displayRemises, displayTaxes, editingId, eligForm, getAuthHeader, handleAddRemise, handleAddTaxe, handleArchiveTaxe, handleSaveEligibilite, handleSaveSeuil, handleToggleEligibilite, handleToggleSeuil, handleUpdateTaxe, loadData, newRemise, newTaxe, remises: remises, saving, setEditingId, setEligForm, setNewRemise, setNewTaxe, setSaving, setSeuilForm, setTaxes, seuilForm, taxes: taxes, updatingItem } = ctx;
  return (
    <>
        <TabsContent value="eligibility">
          <BaticoEligibilityConfigPanel variant={activeRcds} />
        </TabsContent>

        {/* SEUILS */}
        <TabsContent value="seuils">
          <div className="space-y-6">
            {/* 1) Seuil de CA d'éligibilité */}
            <Card className="border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-500" />
                  Seuil de CA d'éligibilité — RCDS {activeRcds === 'annuel' ? 'Annuel' : 'Nominatif'}
                </CardTitle>
                <CardDescription>
                  Chiffre d'affaires <strong>minimum</strong> que l'entreprise doit déclarer pour pouvoir
                  souscrire. Sert de filtre d'entrée dans le simulateur (les entreprises dont le CA est
                  inférieur sont rejetées). Indépendant du Montant de base contribution solidaire.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-emerald-50/60 rounded-xl">
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={eligForm[activeRcds]?.actif === true}
                      onCheckedChange={() => handleToggleEligibilite(activeRcds)}
                      data-testid={`v3-elig-${activeRcds}-toggle`}
                    />
                    <span className="font-medium">
                      {eligForm[activeRcds]?.actif ? 'Activé — vérification au calcul' : 'Désactivé — aucun rejet'}
                    </span>
                  </div>
                  <Badge variant={eligForm[activeRcds]?.actif ? 'default' : 'secondary'}>
                    {eligForm[activeRcds]?.actif ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Seuil de CA d'éligibilité (€)</Label>
                    <Input
                      type="number"
                      step="100"
                      min="0"
                      value={eligForm[activeRcds]?.seuil_ca ?? ''}
                      onChange={(e) => setEligForm(prev => ({
                        ...prev,
                        [activeRcds]: { ...prev[activeRcds], seuil_ca: e.target.value }
                      }))}
                      className="text-lg font-mono mt-1"
                      data-testid={`v3-elig-${activeRcds}-input`}
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Valeur par défaut : 40 000 € (Annuel) / 5 000 € (Nominatif). Toute entreprise dont
                      le CA déclaré est strictement inférieur à cette valeur recevra une erreur
                      d'éligibilité explicite.
                    </p>
                  </div>
                  <div>
                    <Label>Message d'erreur d'éligibilité</Label>
                    <Input
                      value={eligForm[activeRcds]?.message || ''}
                      onChange={(e) => setEligForm(prev => ({
                        ...prev,
                        [activeRcds]: { ...prev[activeRcds], message: e.target.value }
                      }))}
                      className="mt-1"
                      placeholder="Ex: CA minimum 40 000 € requis pour RCDS Annuel"
                      data-testid={`v3-elig-${activeRcds}-message`}
                    />
                  </div>
                </div>

                <Button
                  onClick={() => handleSaveEligibilite(activeRcds)}
                  disabled={saving}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid={`v3-elig-${activeRcds}-save`}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Sauvegarder le seuil d'éligibilité
                </Button>
              </CardContent>
            </Card>

            {/* 2) Montant de base contribution solidaire (plancher) */}
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="h-5 w-5 text-amber-500" />
                  Montant de base contribution solidaire — RCDS {activeRcds === 'annuel' ? 'Annuel' : 'Nominatif'}
                </CardTitle>
                <CardDescription>
                  Valeur plancher (en €) reportée sur les Conditions Particulières et utilisée comme
                  cotisation minimale facturée. Si la cotisation calculée passe sous ce montant, elle
                  est alignée sur ce plancher. <strong>Distinct du seuil d'éligibilité</strong> ci-dessus.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={seuilForm[activeRcds]?.actif !== false}
                      onCheckedChange={() => handleToggleSeuil(activeRcds)}
                      data-testid={`v3-seuil-${activeRcds}-toggle`}
                    />
                    <span className="font-medium">
                      {seuilForm[activeRcds]?.actif !== false ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                  <Badge variant={seuilForm[activeRcds]?.actif ? 'default' : 'secondary'}>
                    {seuilForm[activeRcds]?.actif ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Montant de base de la contribution solidaire (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={seuilForm[activeRcds]?.seuil_minimum || ''}
                      onChange={(e) => setSeuilForm(prev => ({
                        ...prev,
                        [activeRcds]: { ...prev[activeRcds], seuil_minimum: e.target.value }
                      }))}
                      className="text-lg font-mono mt-1"
                      data-testid={`v3-seuil-${activeRcds}-input`}
                    />
                    <p className="text-[11px] text-slate-500 mt-1">
                      Valeur plancher utilisée pour la cotisation minimum sur les Conditions Particulières
                      (PDF) et la facturation. Décimales acceptées (centimes).
                    </p>
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Input
                      value={seuilForm[activeRcds]?.message_seuil || ''}
                      onChange={(e) => setSeuilForm(prev => ({
                        ...prev,
                        [activeRcds]: { ...prev[activeRcds], message_seuil: e.target.value }
                      }))}
                      className="mt-1"
                      data-testid={`v3-seuil-${activeRcds}-message`}
                    />
                  </div>
                </div>

                <Button
                  onClick={() => handleSaveSeuil(activeRcds)}
                  disabled={saving}
                  className="w-full"
                  data-testid={`v3-seuil-${activeRcds}-save`}
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  Sauvegarder le montant de base
                </Button>
              </CardContent>
            </Card>

            {/* 3) Outil de synchronisation cross-config (V3 → V2 + legacy) */}
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <RefreshCw className="h-4 w-4 text-blue-600" />
                  Synchronisation cross-configuration
                </CardTitle>
                <CardDescription className="text-xs">
                  Force la propagation des seuils V3 (ci-dessus) vers les anciennes collections
                  utilisées par certains écrans historiques. Utile si vous constatez un écart
                  entre le simulateur et cette page après une migration.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      setSaving(true);
                      const res = await axios.post(
                        `${API}/api/batico/config/v3/seuils-ca/sync-from-v3`,
                        {},
                        { headers: getAuthHeader() }
                      );
                      if (res.data?.success) {
                        toast.success(
                          `Synchro OK : ${res.data.v2_updates} v2, ${res.data.params_updates} legacy`
                        );
                      } else {
                        toast.error(res.data?.error || 'Échec de la synchro');
                      }
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Erreur de synchronisation');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  className="w-full border-blue-300 bg-white hover:bg-blue-50 text-blue-800"
                  data-testid="v3-sync-from-v3-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Forcer la synchronisation V3 → V2 + legacy
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TAXES */}
        <TabsContent value="taxes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-orange-500" />
                Contributions solidaires - RCDS {activeRcds === 'annuel' ? 'Annuel' : 'Nominatif'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {displayTaxes.map(taxe => (
                <div key={taxe.id} className={`p-4 rounded-xl transition-all ${editingId === taxe.id ? 'bg-orange-50 border-2 border-orange-300' : 'bg-slate-50'}`}>
                  {editingId === taxe.id ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <Label className="text-xs text-slate-500">Libelle</Label>
                          <Input
                            value={taxe.nom}
                            onChange={(e) => setTaxes(prev => prev.map(t => t.id === taxe.id ? { ...t, nom: e.target.value } : t))}
                            className="text-sm"
                            data-testid={`v3-contrib-nom-${taxe.id}`}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-slate-500">Taux (%)</Label>
                          <Input
                            type="number" step="0.50" min="0" max="100"
                            value={taxe.taux}
                            onChange={(e) => setTaxes(prev => prev.map(t => t.id === taxe.id ? { ...t, taux: parseFloat(e.target.value) || 0 } : t))}
                            className="text-sm text-center font-mono"
                            data-testid={`v3-contrib-taux-${taxe.id}`}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label className="text-xs text-slate-500">Description</Label>
                          <Input
                            value={taxe.description || ''}
                            onChange={(e) => setTaxes(prev => prev.map(t => t.id === taxe.id ? { ...t, description: e.target.value } : t))}
                            className="text-sm"
                          />
                        </div>
                        <div className="col-span-2 flex items-end gap-1">
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => { handleUpdateTaxe(taxe.id, taxe); setEditingId(null); }}>
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setEditingId(null); loadData(); }}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-10 bg-orange-100 text-orange-700 rounded-lg flex items-center justify-center font-bold">
                          {taxe.taux}%
                        </div>
                        <div>
                          <div className="font-medium">{taxe.nom}</div>
                          <div className="text-sm text-slate-500">{taxe.description}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {updatingItem === taxe.id && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                        <Switch
                          checked={taxe.actif}
                          disabled={updatingItem === taxe.id}
                          onCheckedChange={(checked) => handleUpdateTaxe(taxe.id, { ...taxe, actif: checked })}
                        />
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(taxe.id)} data-testid={`v3-edit-${taxe.id}`}>
                          <Settings className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button size="sm" variant="ghost" disabled={updatingItem === taxe.id} onClick={() => handleArchiveTaxe(taxe.id)} data-testid={`v3-delete-${taxe.id}`}>
                          <Archive className="h-4 w-4 text-orange-500" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              <div className="p-4 border-2 border-dashed border-slate-300 rounded-xl">
                <div className="grid grid-cols-4 gap-2">
                  <Input placeholder="Nom" value={newTaxe.nom} onChange={(e) => setNewTaxe(p => ({ ...p, nom: e.target.value }))} />
                  <Input type="number" placeholder="Taux %" value={newTaxe.taux} onChange={(e) => setNewTaxe(p => ({ ...p, taux: e.target.value }))} />
                  <Input placeholder="Description" value={newTaxe.description} onChange={(e) => setNewTaxe(p => ({ ...p, description: e.target.value }))} />
                  <Button onClick={handleAddTaxe} disabled={saving}><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
                </div>
              </div>
              
              <div className="p-3 bg-orange-50 rounded-xl text-center">
                <span className="text-lg font-bold text-orange-700">
                  Total: {displayTaxes.filter(t => t.actif).reduce((s, t) => s + t.taux, 0).toFixed(2)}%
                </span>
                <span className="text-xs text-orange-500 ml-2">
                  ({displayTaxes.filter(t => t.actif).length} contribution{displayTaxes.filter(t => t.actif).length > 1 ? 's' : ''})
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REMISES */}
        <TabsContent value="remises">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-green-500" />
                Remises - RCDS {activeRcds === 'annuel' ? 'Annuel' : 'Nominatif'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {displayRemises.map(remise => (
                <div key={remise.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-10 bg-green-100 text-green-700 rounded-lg flex items-center justify-center font-bold">
                      -{remise.pourcentage}%
                    </div>
                    <div>
                      <div className="font-medium">{remise.nom}</div>
                      <div className="text-sm text-slate-500">{remise.conditions}</div>
                    </div>
                  </div>
                  <Switch checked={remise.actif !== false} />
                </div>
              ))}
              
              <div className="p-4 border-2 border-dashed border-slate-300 rounded-xl">
                <div className="grid grid-cols-4 gap-2">
                  <Input placeholder="Nom" value={newRemise.nom} onChange={(e) => setNewRemise(p => ({ ...p, nom: e.target.value }))} />
                  <Input type="number" placeholder="%" value={newRemise.pourcentage} onChange={(e) => setNewRemise(p => ({ ...p, pourcentage: e.target.value }))} />
                  <Input placeholder="Conditions" value={newRemise.conditions} onChange={(e) => setNewRemise(p => ({ ...p, conditions: e.target.value }))} />
                  <Button onClick={handleAddRemise}><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COEFFICIENTS */}
    </>
  );
}
